import React, { FC, ReactNode } from 'react';
import {
  Auth0Context,
  Auth0ContextInterface,
  initialContext,
  User,
} from '@auth0/auth0-react';
import type { GetTokenSilentlyVerboseResponse } from '@auth0/auth0-spa-js';
import { AUTH_DISABLED_USER_SUB } from '@shared/constants/auth-disabled.constants';
import { isLocalHostname } from '@shared/helpers/environment.helpers';

/**
 * True when the client runs with authentication disabled (e2e tests).
 * The server must run with the matching AUTH_DISABLED=true env variable.
 *
 * Fails closed: the flag is only honored on a local Vite dev server, so a
 * bundle built with it set can never authenticate itself. The build refuses the
 * flag too, see the auth-disabled guard in vite.config.mts.
 */
export const isAuthDisabled = (): boolean => {
  if (import.meta.env.VITE_AUTH_DISABLED !== 'true') return false;
  if (import.meta.env.DEV && isLocalHostname(window.location.hostname))
    return true;

  console.error(
    'VITE_AUTH_DISABLED=true ignored — authentication can only be disabled on a local dev server',
  );
  return false;
};

const FAKE_TOKEN_TTL_SECONDS = 12 * 60 * 60;

const encodeBase64Url = (value: object): string =>
  btoa(JSON.stringify(value))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

/**
 * Build an unsigned JWT that jwtDecode can parse (used by Auth0TokenRefresher
 * to read `sub` and `exp`). The server never verifies it when auth is disabled.
 */
const createFakeAccessToken = (): string => {
  const header = { alg: 'none', typ: 'JWT' };
  const payload = {
    sub: AUTH_DISABLED_USER_SUB,
    exp: Math.floor(Date.now() / 1000) + FAKE_TOKEN_TTL_SECONDS,
  };
  return `${encodeBase64Url(header)}.${encodeBase64Url(payload)}.`;
};

const authDisabledUser: User = {
  sub: AUTH_DISABLED_USER_SUB,
  name: 'E2E Test User',
  nickname: 'e2e',
  email: 'e2e@nodon.se',
  picture: '',
};

// Overload signatures matching Auth0ContextInterface['getAccessTokenSilently']
function getAccessTokenSilently(options: {
  detailedResponse: true;
}): Promise<GetTokenSilentlyVerboseResponse>;
function getAccessTokenSilently(options?: object): Promise<string>;
async function getAccessTokenSilently(options?: {
  detailedResponse?: boolean;
}): Promise<string | GetTokenSilentlyVerboseResponse> {
  const accessToken = createFakeAccessToken();
  if (options?.detailedResponse) {
    return {
      access_token: accessToken,
      id_token: '',
      expires_in: FAKE_TOKEN_TTL_SECONDS,
      token_type: 'Bearer',
    };
  }
  return accessToken;
}

const authDisabledContext: Auth0ContextInterface = {
  ...initialContext,
  isAuthenticated: true,
  isLoading: false,
  user: authDisabledUser,
  getAccessTokenSilently,
  logout: () => Promise.resolve(),
  loginWithRedirect: () => Promise.resolve(),
};

type AuthDisabledProviderProps = {
  children: ReactNode;
};

/**
 * Replaces Auth0Provider when {@link isAuthDisabled} — provides a permanently
 * authenticated Auth0 context so useAuth0/withAuthenticationRequired consumers
 * work without a real Auth0 session.
 */
export const AuthDisabledProvider: FC<AuthDisabledProviderProps> = ({
  children,
}) => (
  <Auth0Context.Provider value={authDisabledContext}>
    {children}
  </Auth0Context.Provider>
);
